import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api, { fmt, fmtQty, fmtDate, daysUntil } from '../../utils/api';

const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

export default function OrdersPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canEditExpenses = hasRole('admin', 'accounting', 'account_manager');
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [filters, setFilters] = useState({ docType: '', origin: '', search: '' });
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedCard, setExpandedCard] = useState(null);
  const toggleCard = (key) => setExpandedCard(expandedCard === key ? null : key);
  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const initialLoad = useRef(true);
  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (filters.docType) params.docType = filters.docType;
      if (filters.origin) params.origin = filters.origin;
      if (filters.search) params.search = filters.search;
      const [ordersRes, statsRes, payRes] = await Promise.all([
        api.get('/orders', { params }),
        api.get('/orders/stats', { params }),
        api.get('/orders/payment-summary', { params }),
      ]);
      setOrders(ordersRes.data.orders || ordersRes.data || []);
      setStats(statsRes.data);
      setPayment(payRes.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      fetchData();
    } else {
      fetchData(true);
    }
  }, [fetchData]);

  const toggle = (no) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(no) ? next.delete(no) : next.add(no);
    return next;
  });

  const toggleAll = (open) => {
    setExpanded(open ? new Set(orders.map(o => o.orderNo)) : new Set());
  };

  const saveShipping = async (orderNo, value) => {
    const shipping = parseFloat(value) || 0;
    try {
      await api.put(`/orders/${orderNo}/shipping`, { shipping });
      setOrders(prev => prev.map(o => o.orderNo === orderNo ? { ...o, shipping } : o));
    } catch {}
  };

  if (loading) return <div className="loading"><div className="spinner" />{t('common.loading')}</div>;

  return (
    <div>
      {/* Stats */}
      <div className="summary-cards">
        {[
          { key: 'shipped', label: t('orders.shipped'), value: fmt(stats?.shipped?.total), sub: `${stats?.shipped?.lines || 0} ${t('common.lines')}`, color: '#1565c0' },
          { key: 'pending', label: t('orders.pending'), value: fmt(stats?.pending?.total), sub: `${stats?.pending?.lines || 0} ${t('common.lines')}`, color: '#1565c0' },
          { key: 'comps', label: t('orders.compensations'), value: fmt(stats?.compensations?.total), sub: `${stats?.compensations?.lines || 0} ${t('common.lines')}`, color: '#1565c0' },
          { key: 'net', label: t('orders.netTotal'), value: fmt(stats?.netTotal), sub: `${fmtQty(stats?.totalUnits)} ${t('common.units')}`, color: '#1565c0' },
        ].map(card => (
          <div key={card.key} className="summary-card clickable" style={{ background: '#fff', outline: expandedCard === card.key ? '2px solid var(--gold)' : 'none', position: 'relative' }}
            onClick={() => toggleCard(card.key)}>
            <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>ⓘ</div>
            <div className="label">{card.label}</div>
            <div className="value" style={{ color: card.color }}>{card.value}</div>
            <div className="sub">{card.sub}</div>
          </div>
        ))}
      </div>
      {expandedCard && ['shipped', 'pending', 'comps', 'net'].includes(expandedCard) && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Order</th><th>Origin</th><th>Type</th><th className="text-end">Lines</th><th className="text-end">Units</th><th className="text-end">Amount</th></tr></thead>
            <tbody>
              {orders.filter(o => {
                if (expandedCard === 'shipped') return o.docType === 'SI' && !o.lines?.some(l => l.isCompensation);
                if (expandedCard === 'pending') return o.docType === 'SO' && !o.lines?.some(l => l.isCompensation);
                if (expandedCard === 'comps') return o.lines?.some(l => l.isCompensation);
                return true;
              }).map(o => (
                <tr key={o.orderNo}>
                  <td className="fw-bold">{o.orderNo}</td>
                  <td><span className={`badge badge-${(o.origin || '').toLowerCase()}`}>{o.origin}</span></td>
                  <td><span className={`badge badge-${(o.docType || '').toLowerCase()}`}>{o.docType}</span></td>
                  <td className="text-end">{o.lines?.length || 0}</td>
                  <td className="text-end">{fmtQty(o.totals?.units)}</td>
                  <td className="text-end fw-bold">{fmt(o.totals?.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* Payment Due */}
      {payment && (
        <div className="summary-cards">
          {[
            { key: 'dueOverdue', label: t('orders.overdue'), value: fmt(payment.overdue || 0), color: '#dc2626' },
            { key: 'due30', label: t('orders.within30'), value: fmt(payment.within30), color: '#f97316' },
            { key: 'due60', label: t('orders.between30_60'), value: fmt(payment.between30_60), color: '#f59e0b' },
            { key: 'due90', label: t('orders.over90'), value: fmt(payment.over90), color: '#16a34a' },
          ].map(card => (
            <div key={card.key} className="summary-card clickable" style={{ background: '#fff', outline: expandedCard === card.key ? '2px solid var(--gold)' : 'none', position: 'relative' }}
              onClick={() => toggleCard(card.key)}>
              <div style={{ position: 'absolute', top: 10, right: 12, fontSize: 10, color: 'var(--text-muted)', opacity: 0.6 }}>ⓘ</div>
              <div className="label">{card.label}</div>
              <div className="value" style={{ color: card.color }}>{card.value}</div>
            </div>
          ))}
        </div>
      )}
      {expandedCard && ['dueOverdue', 'due30', 'due60', 'due90'].includes(expandedCard) && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="table-wrap"><table>
            <thead><tr><th>Order</th><th>Origin</th><th className="text-end">Amount</th><th className="text-end">Due Date</th><th className="text-end">Days</th></tr></thead>
            <tbody>
              {orders.filter(o =>
                o.docType === 'SI' &&
                !o.lines?.some(l => l.isCompensation) &&
                o.paymentStatus !== 'paid'
              ).map(o => {
                const due = o.lines?.[0]?.paymentDue;
                const days = daysUntil(due);
                if (days === null) return null;
                if (expandedCard === 'dueOverdue' && days >= 0) return null;
                if (expandedCard === 'due30' && (days < 0 || days > 30)) return null;
                if (expandedCard === 'due60' && (days <= 30 || days > 60)) return null;
                if (expandedCard === 'due90' && days <= 60) return null;
                return (
                  <tr key={o.orderNo}>
                    <td className="fw-bold">{o.orderNo}</td>
                    <td><span className={`badge badge-${(o.origin || '').toLowerCase()}`}>{o.origin}</span></td>
                    <td className="text-end fw-bold">{fmt(o.totals?.amount)}</td>
                    <td className="text-end">{due ? fmtDate(due) : '—'}</td>
                    <td className="text-end" style={{ color: days <= 0 ? '#dc2626' : days <= 30 ? '#f59e0b' : '#16a34a', fontWeight: 700 }}>{days}d</td>
                  </tr>
                );
              }).filter(Boolean)}
            </tbody>
          </table></div>
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <select className="input" value={filters.docType} onChange={e => setFilters(f => ({ ...f, docType: e.target.value }))}>
          <option value="">{t('orders.all')} Types</option>
          <option value="SI">SI</option>
          <option value="SO">SO</option>
          <option value="COMP">Comp</option>
        </select>
        <select className="input" value={filters.origin} onChange={e => setFilters(f => ({ ...f, origin: e.target.value }))}>
          <option value="">{t('orders.all')} {t('logistics.origin')}</option>
          <option value="EU">EU</option>
          <option value="CN">CN</option>
        </select>
        <input
          className="input"
          placeholder={t('orders.search')}
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          style={{ maxWidth: 300 }}
        />
        <button className="btn btn-outline btn-sm" onClick={() => toggleAll(true)}>{t('orders.expandAll')}</button>
        <button className="btn btn-outline btn-sm" onClick={() => toggleAll(false)}>{t('orders.collapseAll')}</button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>{t('common.sort')}:</span>
        {[['date',t('common.date')],['units',t('warehouse.qty')],['value',t('common.value')]].map(([key,label]) => (
          <button key={key} className={`btn btn-sm ${sortBy === key ? 'btn-gold' : 'btn-outline'}`}
            style={{ fontSize: 11 }} onClick={() => toggleSort(key)}>
            {label} {sortBy === key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
          </button>
        ))}
      </div>

      {/* Orders */}
      {orders.length === 0 ? (
        <div className="empty-state">{t('common.noData')}</div>
      ) : [...orders].sort((a, b) => {
        let va, vb;
        switch (sortBy) {
          case 'date':
            va = a.lines?.[0]?.orderDate ? new Date(a.lines[0].orderDate).getTime() : 0;
            vb = b.lines?.[0]?.orderDate ? new Date(b.lines[0].orderDate).getTime() : 0;
            break;
          case 'units': va = a.totals?.units || 0; vb = b.totals?.units || 0; break;
          case 'value': va = a.totals?.amount || 0; vb = b.totals?.amount || 0; break;
          default: va = 0; vb = 0;
        }
        return sortDir === 'asc' ? va - vb : vb - va;
      }).map(order => {
        const orderMonth = order.lines?.[0]?.orderDate ? MONTHS[new Date(order.lines[0].orderDate).getMonth()] : '';
        return (
        <div key={order.orderNo} className="order-group">
          <div className="order-group-header" onClick={() => toggle(order.orderNo)}
            style={{ display: 'grid', gridTemplateColumns: '24px 120px 50px 50px 50px 1fr auto auto auto', alignItems: 'center', gap: 10 }}>
            <span className={`arrow${expanded.has(order.orderNo) ? ' open' : ''}`}>&#9654;</span>
            <span className="order-no">{order.orderNo}</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{order.lines?.[0]?.orderDate ? fmtDate(order.lines[0].orderDate) : 'TBA'}</span>
            <span className={`badge badge-${(order.origin || '').toLowerCase()}`}>{order.origin}</span>
            <span className={`badge badge-${(order.docType || '').toLowerCase()}`}>{order.docType}</span>
            <span className="text-muted" style={{ fontSize: 13 }}>{fmtQty(order.totals?.units)} {t('common.units')}</span>
            <span className="amount">{fmt(order.totals?.amount)}</span>
            {order.paymentStatus === 'paid' && (
              <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 800, background: '#dcfce7', color: '#16a34a', border: '2px solid #16a34a', letterSpacing: 1 }}>PAID</span>
            )}
            {(() => {
              if (order.docType === 'SO' || order.paymentStatus === 'paid') return <span style={{ minWidth: 90 }} />;
              const due = order.lines?.[0]?.paymentDue;
              const days = daysUntil(due);
              const color = days !== null ? (days <= 0 ? '#c62828' : days <= 30 ? '#e65100' : '#2e7d32') : '#999';
              return (
                <span style={{ fontSize: 11, fontWeight: 600, color, minWidth: 90, textAlign: 'right' }}>
                  {due ? `${fmtDate(due)} (${days}d)` : ''}
                </span>
              );
            })()}
            <span onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#6c757d' }}>{t('expenses.shipping')}:</span>
              {canEditExpenses ? (
                <input className="inline-edit" type="number" step="0.01"
                  style={{ width: 80, textAlign: 'right', fontSize: 12 }}
                  key={`ship-${order.orderNo}-${order.shipping}`}
                  defaultValue={order.shipping || ''}
                  placeholder="0"
                  onBlur={e => {
                    const v = parseFloat(e.target.value) || 0;
                    if (v !== (order.shipping || 0)) saveShipping(order.orderNo, v);
                  }}
                  onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                />
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600 }}>{order.shipping ? fmt(order.shipping) : '—'}</span>
              )}
            </span>
          </div>
          {expanded.has(order.orderNo) && (
            <div className="order-group-body">
              <div style={{ display: 'flex', gap: 8, padding: '8px 12px', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
                <OrderInvoice orderNo={order.orderNo} />
                <div style={{ flex: 1 }} />
                {order.docType === 'SI' && (
                  order.paymentStatus === 'paid' ? (
                    <button className="btn btn-sm" style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #16a34a', fontSize: 11, fontWeight: 700 }}
                      onClick={async () => {
                        if (!window.confirm('Mark as unpaid?')) return;
                        try { await api.put(`/expenses/${order.orderNo}/payment`, { status: 'unpaid' }); fetchData(true); }
                        catch (err) { alert('Failed'); }
                      }}>✓ PAID — click to undo</button>
                  ) : (
                    <button className="btn btn-sm" style={{ background: '#fff', color: '#dc2626', border: '1px solid #dc2626', fontSize: 11, fontWeight: 700 }}
                      onClick={async () => {
                        if (!window.confirm(`Mark ${order.orderNo} as PAID?`)) return;
                        try { await api.put(`/expenses/${order.orderNo}/payment`, { status: 'paid' }); fetchData(true); }
                        catch (err) { alert('Failed'); }
                      }}>💰 Mark as Paid</button>
                  )
                )}
                <button className="btn btn-sm btn-outline" style={{ fontSize: 11, color: '#1565c0' }}
                  onClick={async () => {
                    if (!window.confirm(`Send shipping cost request email for ${order.orderNo}?`)) return;
                    try {
                      await api.post('/data-requests', { type: 'shipping_cost', reference: order.orderNo });
                      alert(`Email sent for ${order.orderNo}!`);
                    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                  }}>
                  📧 Ask Shipping Cost
                </button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>SKU / Barcode</th>
                      <th>Description</th>
                      <th className="text-end">Qty</th>
                      <th className="text-end">Unit Price</th>
                      <th className="text-end">Amount</th>
                      {order.docType !== 'SO' && <th className="text-end">{t('orders.paymentDue')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(order.lines || []).map((line, i) => {
                      const days = daysUntil(line.paymentDue);
                      const dueClass = days !== null ? (days <= 0 ? 'text-danger' : days <= 30 ? 'text-warning' : 'text-muted') : '';
                      return (
                        <tr key={i}>
                          <td>{line.sku}{line.barcode && line.barcode !== line.sku && <div className="text-muted" style={{ fontSize: 10 }}>{line.barcode}</div>}</td>
                          <td>{line.description}</td>
                          <td className="text-end">{fmtQty(line.quantity)}</td>
                          <td className="text-end">{fmt(line.unitPrice)}</td>
                          <td className="text-end">{fmt(line.amount)}</td>
                          {order.docType !== 'SO' && <td className={`text-end ${dueClass}`}>
                            {line.paymentDue ? `${fmtDate(line.paymentDue)} (${days}d)` : '--'}
                          </td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function OrderInvoice({ orderNo }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/data-requests/files/${orderNo}`).then(res => setFiles(res.data || [])).catch(() => {});
  }, [orderNo]);

  const invoice = files.find(f => f.fileType === 'invoice');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('files', file);
    try {
      await api.post('/data-requests/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const res = await api.get(`/data-requests/files/${orderNo}`);
      setFiles(res.data || []);
    } catch (err) { alert('Upload failed'); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Invoice:</span>
      {invoice ? (
        <>
          <button className="btn btn-sm btn-outline" style={{ fontSize: 12, color: '#1565c0', fontWeight: 600 }}
            onClick={async () => {
              try {
                const res = await api.get(`/data-requests/download/${invoice.id}`, { responseType: 'blob' });
                const isPdf = invoice.originalName.toLowerCase().endsWith('.pdf');
                const blob = new Blob([res.data], { type: isPdf ? 'application/pdf' : 'application/octet-stream' });
                const url = window.URL.createObjectURL(blob);
                if (isPdf) {
                  window.open(url, '_blank');
                } else {
                  const a = document.createElement('a');
                  a.href = url; a.download = invoice.originalName; a.click();
                }
              } catch { alert('Failed to download'); }
            }}>
            📄 {invoice.originalName}
          </button>
          <button className="btn btn-sm btn-outline" style={{ fontSize: 10, padding: '2px 6px' }}
            onClick={() => fileRef.current?.click()}>Replace</button>
        </>
      ) : (
        <button className="btn btn-sm btn-outline" style={{ fontSize: 11 }}
          onClick={() => fileRef.current?.click()}>
          {uploading ? 'Uploading...' : '📎 Upload Invoice'}
        </button>
      )}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.png" style={{ display: 'none' }} onChange={handleUpload} />
    </div>
  );
}
